import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import PaperDraggable from "../../../../../../../../../components/PaperDraggable";
import { TLotesSelecionados, TProdutosDetalhes } from "../../../../../../../../../types/api/comercial/detalhar-pedido";
import { enqueueSnackbar } from "notistack";
import { permitirApenasNumeros } from "../../../../../../../../../helpers/masks";
import { DadoValorPrinter } from "../../../../../../../../../components/BasicComponents/DisplayDados/DadoValorPrinter";
import { FormProvider, useForm } from "react-hook-form";
import QuantidadeLotes from "./QuantidadeLotes";

export default function DialogQuantidadeExpedir({
    open,
    data,
    handleClose,
    handleChangeQuantidade
}: DialogQuantidadeExpedirProps
) {
    //Valores importantes
    const possuiLotes = data.exige_lote

    //Iniciando o hook-form
    const methods = useForm<TInternForm>({
        defaultValues: {
            internItem: data,
            lotes_selecionados: data.lotes_selecionados,
            quantidade_para_expedir: data.quantidade_para_expedir || data.quantidade - data.quantidade_entregue,
        }
    })
    const { handleSubmit, watch, setValue } = methods

    const internItem = watch("internItem")
    const quantidadeParaExpedir = watch("quantidade_para_expedir")

    const onsubmit = (data: TInternForm) => {
        //Realizando as validações necessárias para a expedição
        const quantidade = data.quantidade_para_expedir?.toString()

        if (quantidade === "NaN" || quantidade === "0" || !quantidade || quantidade === "") {
            enqueueSnackbar(
                'Favor incluir apenas valores númericos nos campos de quantidades',
                { variant: 'error' }
            )
            return
        }
        if ((!possuiLotes) && (Number(quantidade) > (data.internItem.quantidade - data.internItem.quantidade_entregue))) {
            enqueueSnackbar(
                'A quantidade para expedir não pode ser maior que a quantidade comprada',
                { variant: 'warning' }
            )
            return
        }

        if (possuiLotes && (!data.lotes_selecionados || data.lotes_selecionados.length === 0)) {
            enqueueSnackbar(
                'Favor selecionar os lotes a expedir',
                { variant: 'warning' }
            )
            return
        }

        if (possuiLotes && data.lotes_selecionados) {
            const quantidadeLotesSelecionados = data.lotes_selecionados.reduce((acc, current) => acc + current.quantidade, 0);

            const lotesDisponiveis = data.internItem.lotes_disponiveis

            const loteInvalido = data.lotes_selecionados.find((loteSelecionado) => {
                const loteDisponivel = lotesDisponiveis.find((lote) => lote.lote === loteSelecionado.lote);
                return loteDisponivel && loteDisponivel.estoque_atual < loteSelecionado.quantidade;
            });

            if (loteInvalido) {
                enqueueSnackbar(
                    `O estoque atual do lote ${loteInvalido.lote} não pode ser menor que a quantidade selecionada (${loteInvalido.quantidade}).`,
                    { variant: 'warning' }
                );
                return;
            }

            if (quantidadeLotesSelecionados === 0) {
                enqueueSnackbar(
                    'Favor incluir a quantidade de lotes a expedir',
                    { variant: 'warning' }
                )
                return
            }

            if (quantidadeLotesSelecionados > (data.internItem.quantidade - data.internItem.quantidade_entregue)) {
                enqueueSnackbar(
                    `A soma das quantidades dos lotes a expedir (${quantidadeLotesSelecionados}) não pode exceder a quantidade restante disponível para expedição (${data.internItem.quantidade - data.internItem.quantidade_entregue}).`,
                    { variant: 'warning' }
                )
                return
            }
            const item = { ...internItem, lotes_selecionados: data.lotes_selecionados.filter((lote) => lote.quantidade > 0,), quantidade_para_expedir: quantidadeLotesSelecionados }
            handleChangeQuantidade(item)
        }

        if (!possuiLotes && quantidade) {
            const item = { ...internItem, quantidade_para_expedir: Number(quantidade) }
            handleChangeQuantidade(item)
        }

        handleClose()
    }

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
                fullWidth
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Expedir itens</DialogTitle>
                <DialogContent>
                    <Stack direction={'row'} gap={2} justifyContent={'space-between'} mb={2}>
                        <Box>
                            <Typography fontWeight="bold">{internItem.nome}</Typography>
                            <Typography variant="caption">Cód.: {internItem.produto_id} </Typography>
                        </Box>
                        <DadoValorPrinter
                            titulo="Quantidade comprada"
                            valor={`${data.quantidade}`}
                            alignValue="right"
                        />
                    </Stack>

                    <TextField
                        margin="normal"
                        fullWidth
                        disabled={possuiLotes}
                        value={quantidadeParaExpedir}
                        onChange={({ target }) => setValue("quantidade_para_expedir", Number(permitirApenasNumeros(target.value)))}
                        label="Quantidade"
                        variant="outlined"
                        type="number"
                    />
                    {
                        possuiLotes &&
                        <QuantidadeLotes data={internItem} />
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Fechar
                    </Button>
                    <Button onClick={handleSubmit(onsubmit)} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>

    )
}

interface DialogQuantidadeExpedirProps {
    open: boolean;
    handleClose: () => void;
    handleChangeQuantidade: (item: TProdutosDetalhes) => void;
    data: TProdutosDetalhes
}

type TInternForm = {
    internItem: TProdutosDetalhes,
    lotes_selecionados: TLotesSelecionados,
    quantidade_para_expedir: number | string
}
